import * as React from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router";
import { AuthGate } from "@/auth-gate";
import { FeatureFlagGate } from "@/components/feature-flag-gate";
import { ProtectedRoute } from "@/components/protected-route";
import { ShareUrlHandler } from "@/components/share-url-handler";
import {
  useCustomChannelsVisibility,
  useMiniAppsVisibility,
} from "@/hooks/use-feature-flags";
import AppLayout from "@/layouts/app-layout";
import AccountActivity from "@/pages/accounts/account-explorer";
import AccountListView from "@/pages/accounts/account-list";
import CampaignsPage from "@/pages/campaigns/campaign-explorer";
import CampaignListView from "@/pages/campaigns/campaign-list";
import ChannelsPage from "@/pages/channels/channel-explorer";
import ChannelListView from "@/pages/channels/channel-list";
import CustomChannelsExplorer from "@/pages/custom-channels/custom-channels-explorer";
import CustomChannelsListView from "@/pages/custom-channels/custom-channels-list";
import CustomChannelsRules from "@/pages/custom-channels/rules";
import { FivetranConnection } from "@/pages/fivetran/fivetran-connection";
import HomePage from "@/pages/home";
import OpportunityListView from "@/pages/opportunities/opportunity-list";
import PersonExplorer from "@/pages/persons/person-explorer";
import PersonListView from "@/pages/persons/person-list";
import SalespeopleListView from "@/pages/salespeople/salespeople-list";
import SalespersonExplorer from "@/pages/salespeople/salesperson-explorer";
import Settings from "@/pages/settings";
import WebpageExplorer from "@/pages/webpages/webpage-explorer";
import WebpageListView from "@/pages/webpages/webpage-list";
import AppListView from "./pages/apps/app-list";
import AppView from "./pages/apps/app-view";
import AIFieldsListView from "./pages/deep-research/ai-fields";
import DeepResearchListView from "./pages/deep-research/dataset-list";
import DeepResearchReport from "./pages/deep-research/report";

// Wrapper component for custom channels routes that checks feature flag
function CustomChannelsRoute({ children }: { children: React.ReactNode }) {
  const customChannelsVisible = useCustomChannelsVisibility();
  return (
    <FeatureFlagGate isEnabled={customChannelsVisible} redirectTo="/home">
      {children}
    </FeatureFlagGate>
  );
}

// Wrapper component for mini apps / apps routes that checks feature flag
function AppsRoute() {
  const miniAppsVisible = useMiniAppsVisibility();
  return (
    <FeatureFlagGate isEnabled={miniAppsVisible} redirectTo="/home">
      <Outlet />
    </FeatureFlagGate>
  );
}

// Define routes using createBrowserRouter
export const router = createBrowserRouter([
  // Share URL handler - outside main layout for immediate redirect
  {
    path: "/s/:shortId",
    element: <ShareUrlHandler />,
  },
  {
    path: "/",
    element: (
      <React.Suspense>
        <AppLayout />
      </React.Suspense>
    ),
    children: [
      { index: true, element: <Navigate to="/home" replace /> }, // Default route
      { path: "home", element: <HomePage /> },
      // Account routes
      {
        path: "accounts",
        children: [
          { path: "explorer", element: <AccountActivity /> },
          { path: ":accountId/explorer", element: <AccountActivity /> },
          {
            path: ":accountId/milestone-analyses",
            element: <AccountActivity initialTab="milestone-analyses" />,
          },
          { path: "list", element: <AccountListView /> },
        ],
      },
      // Campaign routes
      {
        path: "campaigns",
        children: [
          {
            index: true,
            element: <Navigate to="/campaigns/explorer" replace />,
          },
          { path: "explorer", element: <CampaignsPage /> },
          { path: ":campaignId/explorer", element: <CampaignsPage /> },
          { path: "list", element: <CampaignListView /> },
        ],
      },
      // Channel routes
      {
        path: "channels",
        children: [
          {
            index: true,
            element: <Navigate to="/channels/explorer" replace />,
          },
          { path: "explorer", element: <ChannelsPage /> },
          { path: ":channelId/explorer", element: <ChannelsPage /> },
          { path: "list", element: <ChannelListView /> },
        ],
      },
      // Opportunity routes
      {
        path: "opportunities",
        children: [{ path: "list", element: <OpportunityListView /> }],
      },
      // Person routes
      {
        path: "persons",
        children: [
          { path: "explorer", element: <PersonExplorer /> },
          { path: ":personId/explorer", element: <PersonExplorer /> },
          { path: "list", element: <PersonListView /> },
        ],
      },
      // Deep Research routes
      {
        path: "deep-research",
        children: [
          { index: true, element: <DeepResearchListView /> },
          { path: ":datasetId", element: <DeepResearchReport /> },
        ],
      },
      // AI Fields route
      {
        path: "ai-fields",
        element: <AIFieldsListView />,
      },
      // Apps routes
      {
        path: "apps",
        element: <AppsRoute />,
        children: [
          { index: true, element: <AppListView /> },
          { path: ":slugId", element: <AppView /> },
        ],
      },
      // Salesperson routes
      {
        path: "salespeople",
        children: [
          {
            index: true,
            element: <Navigate to="/salespeople/explorer" replace />,
          },
          { path: "explorer", element: <SalespersonExplorer /> },
          { path: ":personId/explorer", element: <SalespersonExplorer /> },
          { path: "list", element: <SalespeopleListView /> },
        ],
      },
      // Webpages routes
      {
        path: "webpages",
        children: [
          {
            index: true,
            element: <Navigate to="/webpages/list" replace />,
          },
          { path: "explorer", element: <WebpageExplorer /> },
          { path: ":webpageUrl/explorer", element: <WebpageExplorer /> },
          { path: "list", element: <WebpageListView /> },
        ],
      },
      // Custom Channels routes
      {
        path: "custom-channels",
        children: [
          {
            index: true,
            element: (
              <CustomChannelsRoute>
                <Navigate to="/custom-channels/list" replace />
              </CustomChannelsRoute>
            ),
          },
          {
            path: "explorer",
            element: (
              <CustomChannelsRoute>
                <CustomChannelsExplorer />
              </CustomChannelsRoute>
            ),
          },
          {
            path: ":channelId/explorer",
            element: (
              <CustomChannelsRoute>
                <CustomChannelsExplorer />
              </CustomChannelsRoute>
            ),
          },
          {
            path: "list",
            element: (
              <CustomChannelsRoute>
                <CustomChannelsListView />
              </CustomChannelsRoute>
            ),
          },
          {
            path: "rules",
            element: (
              <CustomChannelsRoute>
                <CustomChannelsRules />
              </CustomChannelsRoute>
            ),
          },
        ],
      },
      // Settings routes
      {
        path: "settings",
        children: [
          { index: true, element: <Settings /> },
          { path: "user", element: <Settings /> },
          { path: "company", element: <Settings /> },
          { path: "connections", element: <Settings /> },
          {
            path: "admin",
            element: (
              <ProtectedRoute requireAdmin>
                <Settings />
              </ProtectedRoute>
            ),
          },
          // Individual connection routes for Fivetran onboarding - admin only
          {
            path: "connections/salesforce",
            element: (
              <ProtectedRoute requireAdmin redirectTo="/settings/connections">
                <FivetranConnection service="salesforce" />
              </ProtectedRoute>
            ),
          },
          {
            path: "connections/hubspot",
            element: (
              <ProtectedRoute requireAdmin redirectTo="/settings/connections">
                <FivetranConnection service="hubspot" />
              </ProtectedRoute>
            ),
          },
          {
            path: "connections/marketo",
            element: (
              <ProtectedRoute requireAdmin redirectTo="/settings/connections">
                <FivetranConnection service="marketo" />
              </ProtectedRoute>
            ),
          },
          {
            path: "connections/gongio",
            element: (
              <ProtectedRoute requireAdmin redirectTo="/settings/connections">
                <FivetranConnection service="gongio" />
              </ProtectedRoute>
            ),
          },
        ],
      },

      // Catch-all route
      { path: "*", element: <Navigate to="/home" replace /> },
    ],
  },
]);

export function AppRouter() {
  return (
    <AuthGate>
      <React.Suspense
        fallback={
          <div className="flex h-screen w-screen items-center justify-center">
            Loading…
          </div>
        }
      >
        <RouterProvider router={router} />
      </React.Suspense>
    </AuthGate>
  );
}
